import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo } from "react";

import { FilterId, FilterType } from "@/Components/v2/organism/Header/Header";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useChannels } from "@/pages/channel-roi/api/getChannels";
import { useDateRangeStore } from "@/shared/components/date-range/store/useDateRangeStore";
import { getMappedName } from "@/Utils/mapping-utils";

import { useFlightingStore } from "../store/useFlightingStore";

export const useAvailableFilters = () => {
  const { setTargetNode, setFunnelStage, setChannels, setGeographies } = useFlightingStore();
  const { targetNode, funnelStage, channels, geographies } = useFlightingStore(
    (state) => state.filters
  );
  const { startDate, endDate } = useDateRangeStore();
  const { initInfo: info, nodes } = useInfo();
  const [geoData] = info?.geographies || [];

  const selectedNode = nodes?.getNode(targetNode ?? "");

  const { data: allChannels = [] } = useChannels({
    type: "flighting",
    startDate,
    endDate,
    brand: selectedNode?.node.brand || "",
    category: selectedNode?.node.category || "",
    product: selectedNode?.node.product || "",
    geographies: geographies,
    funnelStages: funnelStage,
    config: {
      enabled: !!startDate && !!endDate && !!selectedNode?.node,
    },
  });

  const channelOptions = useMemo(() => {
    if (!allChannels) return [];

    const options = allChannels
      ?.map((channel) => ({
        label: getMappedName(channel) || channel,
        value: channel,
        selected: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return options;
  }, [allChannels]);

  const handleTargetNodeChange = useCallback(
    (targetNode: string | null) => {
      setTargetNode(targetNode);
    },
    [setTargetNode]
  );

  const handleFunnelStageChange = useCallback(
    (funnelStage: string[]) => setFunnelStage(funnelStage),
    [setFunnelStage]
  );

  const handleChannelsChange = useCallback(
    (channel: string[]) => setChannels(channel),
    [setChannels]
  );

  const handleGeographiesChange = useCallback(
    (geography: string[]) => setGeographies(geography),
    [setGeographies]
  );

  // Memoize each filter option separately
  const targetNodeFilter = useMemo(
    () => ({
      id: FilterId.BusinessUnit,
      label: "Business Unit",
      type: FilterType.BusinessUnit,
      value: targetNode,
      onChange: handleTargetNodeChange,
    }),
    [targetNode, handleTargetNodeChange]
  );

  const funnelStageFilter = useMemo(
    () => ({
      id: FilterId.Funnel,
      label: "Funnels",
      type: FilterType.MultiSelect,
      value: funnelStage,
      onChange: handleFunnelStageChange,
    }),
    [funnelStage, handleFunnelStageChange]
  );

  const channelsFilter = useMemo(
    () => ({
      id: FilterId.Custom,
      label: "Channels",
      type: FilterType.MultiSelect,
      value: channels,
      onChange: handleChannelsChange,
      options: channelOptions,
    }),
    [channels, handleChannelsChange, channelOptions]
  );

  const geographiesFilter = useMemo(
    () => ({
      id: FilterId.Geography,
      label: "Geo",
      type: FilterType.MultiSelect,
      value: geographies,
      onChange: handleGeographiesChange,
    }),
    [geographies, handleGeographiesChange]
  );

  useEffect(() => {
    if (!isEmpty(allChannels) && isEmpty(channels)) {
      setChannels(allChannels || []);
    }
  }, [allChannels]);

  useEffect(() => {
    if (funnelStage.length > 0) {
      setChannels(allChannels || []);
    }
  }, [funnelStage]);

  return useMemo(() => {
    const filters = [targetNodeFilter, channelsFilter, funnelStageFilter];
    if (geoData && geoData.length > 0) {
      filters.push(geographiesFilter);
    }
    return filters;
  }, [targetNodeFilter, funnelStageFilter, channelsFilter, geographiesFilter, geoData]);
};
